import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useTokens } from '../context/ThemeContext';

const OUTLOOK_STEPS = [
  {
    title: 'Create a folder for statements',
    body: 'Right-click in the mailbox folder list and create a new folder named "statements". This folder will store emails that match transaction keywords.',
  },
  {
    title: 'Verify the folder',
    body: 'Confirm that the "statements" folder appears in the folder list. This is where the filtered emails will be moved.',
  },
  {
    title: 'Open Outlook Settings',
    body: 'Click the Settings (gear icon) in the top-right corner of Outlook to configure forwarding and rules.',
  },
  {
    title: 'Enable email forwarding',
    body: 'Navigate to Mail → Forwarding and IMAP. Enable forwarding and enter your Nura ingestion address in the "Forward my email to" field. Keep "Keep a copy of forwarded messages" enabled.',
  },
  {
    title: 'Create a rule to send transactions to Nura',
    body: 'Go to Mail → Rules. Create a new rule named "send transactions to nura". Set the condition: Subject includes keywords like "paid" or "credit". Add two actions: (1) Move to the "statements" folder, and (2) Forward to your Nura ingestion address. Save the rule.',
  },
];

const GMAIL_STEPS = [
  {
    title: 'Coming soon',
    body: 'Gmail forwarding setup instructions will be available shortly. In the meantime, you can set up forwarding manually: go to Gmail Settings → Forwarding and POP/IMAP → Add a forwarding address, and enter your Nura ingestion address.',
  },
];

function StepList({ steps }) {
  const t = useTokens();
  return (
    <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {steps.map((step, i) => (
        <li key={i} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', padding: '14px', backgroundColor: t.chipBg, borderRadius: '10px', border: `1px solid ${t.border}` }}>
          <div style={{
            flexShrink: 0, width: '26px', height: '26px', borderRadius: '50%',
            backgroundColor: t.navActiveText, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '13px', fontWeight: '600',
          }}>
            {i + 1}
          </div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: t.text, marginBottom: '4px' }}>{step.title}</div>
            <div style={{ fontSize: '13px', color: t.textSecondary, lineHeight: '1.5' }}>{step.body}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

function ForwardingSetupModal({ open, onClose, ingestionAddress }) {
  const t = useTokens();
  const [tab, setTab] = useState('outlook');

  if (!open) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
      <div style={{
        backgroundColor: t.surface, borderRadius: '16px', boxShadow: t.cardShadow,
        width: '100%', maxWidth: '560px', maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px', borderBottom: `1px solid ${t.borderLight}`, flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: t.text }}>Set up email forwarding</div>
            <p style={{ fontSize: '12px', color: t.textSecondary, margin: '2px 0 0 0' }}>Forward bank notifications to Nura automatically</p>
          </div>
          <button
            onClick={onClose}
            style={{ width: '32px', height: '32px', borderRadius: '8px', border: 'none', backgroundColor: t.chipBg, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
            aria-label="Close"
          >
            <X size={18} color={t.textSecondary} />
          </button>
        </div>

        {/* Ingestion address reminder */}
        {ingestionAddress && (
          <div style={{ padding: '14px 24px 0', flexShrink: 0 }}>
            <div style={{ backgroundColor: t.chipBg, border: `1px solid ${t.border}`, borderRadius: '10px', padding: '10px 14px' }}>
              <p style={{ fontSize: '12px', color: t.textSecondary, margin: 0, lineHeight: '1.5' }}>
                Your Nura ingestion address: <strong style={{ fontFamily: 'monospace', wordBreak: 'break-all', color: t.navActiveText }}>{ingestionAddress}</strong>
              </p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', padding: '14px 24px 0', flexShrink: 0 }}>
          {['outlook', 'gmail'].map((id) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              style={{
                padding: '7px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                fontSize: '13px', fontWeight: '500',
                backgroundColor: tab === id ? t.navActiveText : t.chipBg,
                color: tab === id ? '#fff' : t.textSecondary,
              }}
            >
              {id === 'outlook' ? 'Outlook' : 'Gmail'}
            </button>
          ))}
        </div>

        {/* Steps */}
        <div style={{ overflowY: 'auto', padding: '16px 24px 24px' }}>
          <StepList steps={tab === 'outlook' ? OUTLOOK_STEPS : GMAIL_STEPS} />
        </div>
      </div>
    </div>
  );
}

export default ForwardingSetupModal;
